import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Loader2,
  Calendar,
  CalendarDays,
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  MapPin,
  BookOpen,
  Globe,
  Building2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const FeriadosPage = () => {
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [scopeFilter, setScopeFilter] = useState('todos');
  const [feriadosMoveis, setFeriadosMoveis] = useState(null);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    scope: 'nacional',
    uf: '',
    municipio: '',
    is_movel: false,
    movel_rule: '',
    ativo: true,
    source: '',
    pendente_confirmacao: false
  });
  
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i);
  
  const scopeOptions = [
    { value: 'nacional', label: 'Nacional', icon: Globe },
    { value: 'estadual', label: 'Estadual', icon: MapPin },
    { value: 'municipal', label: 'Municipal', icon: Building2 },
    { value: 'facultativo', label: 'Facultativo', icon: BookOpen },
  ];
  
  const movelRules = [
    { value: 'pascoa', label: 'Páscoa (Computus)' },
    { value: 'pascoa-48', label: 'Carnaval Segunda (Páscoa - 48)' },
    { value: 'pascoa-47', label: 'Carnaval Terça (Páscoa - 47)' },
    { value: 'pascoa-2', label: 'Sexta-feira Santa (Páscoa - 2)' },
    { value: 'pascoa+60', label: 'Corpus Christi (Páscoa + 60)' },
  ];

  // Carregar feriados
  const loadHolidays = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/holidays?ano=${selectedYear}`);
      setHolidays(res.data.holidays || []);
    } catch (error) {
      console.error('Erro ao carregar feriados:', error);
      // Se não existem feriados, mostrar opção de criar seed
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  // Carregar feriados móveis calculados
  const loadFeriadosMoveis = useCallback(async () => {
    try {
      const res = await api.get(`/holidays/moveis/${selectedYear}`);
      setFeriadosMoveis(res.data);
    } catch (error) {
      console.error('Erro ao carregar móveis:', error);
    }
  }, [selectedYear]);

  useEffect(() => {
    loadHolidays();
    loadFeriadosMoveis();
  }, [loadHolidays, loadFeriadosMoveis]);

  // Seed de feriados padrão
  const handleSeedHolidays = async () => {
    try {
      const res = await api.post('/holidays/seed');
      toast.success(res.data.message);
      loadHolidays();
    } catch (error) {
      toast.error('Erro ao criar feriados padrão');
    }
  };

  // Abrir modal para criar
  const openCreateModal = () => {
    setEditingHoliday(null);
    setFormData({
      date: '',
      name: '',
      scope: 'nacional',
      uf: '',
      municipio: '',
      is_movel: false,
      movel_rule: '',
      ativo: true,
      source: '',
      pendente_confirmacao: false
    });
    setShowModal(true);
  };

  // Abrir modal para editar
  const openEditModal = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      date: holiday.date || '',
      name: holiday.name || '',
      scope: holiday.scope || 'nacional',
      uf: holiday.uf || '',
      municipio: holiday.municipio || '',
      is_movel: holiday.is_movel || false,
      movel_rule: holiday.movel_rule || '',
      ativo: holiday.ativo !== false,
      source: holiday.source || '',
      pendente_confirmacao: holiday.pendente_confirmacao || false
    });
    setShowModal(true);
  };

  // Salvar feriado
  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Nome é obrigatório');
      return;
    }
    if (!formData.is_movel && !formData.date) {
      toast.error('Data é obrigatória para feriados fixos');
      return;
    }
    if (formData.is_movel && !formData.movel_rule) {
      toast.error('Regra móvel é obrigatória para feriados móveis');
      return;
    }
    
    setSaving(true);
    try {
      if (editingHoliday) {
        await api.put(`/holidays/${editingHoliday.id}`, formData);
        toast.success('Feriado atualizado');
      } else {
        await api.post('/holidays', formData);
        toast.success('Feriado criado');
      }
      setShowModal(false);
      loadHolidays();
    } catch (error) {
      toast.error('Erro ao salvar feriado');
    } finally {
      setSaving(false);
    }
  };

  // Excluir feriado
  const handleDelete = async (holidayId, holidayName) => {
    if (!confirm(`Deseja excluir o feriado "${holidayName}"?`)) return;
    
    try {
      await api.delete(`/holidays/${holidayId}`);
      toast.success('Feriado excluído');
      loadHolidays();
    } catch (error) {
      toast.error('Erro ao excluir feriado');
    }
  };

  // Calcular data exibida
  const getDisplayDate = (holiday) => {
    if (holiday.is_movel && holiday.movel_rule && feriadosMoveis) {
      const movel = feriadosMoveis.feriados_moveis?.find(
        m => m.name.includes(holiday.name.split(' ')[0]) || 
             (holiday.movel_rule === m.movel_rule)
      );
      if (movel) {
        return movel.date;
      }
      return `Calculado (${holiday.movel_rule})`;
    }
    
    if (holiday.date?.length === 5) {
      return `Todo ano: ${holiday.date.split('-').reverse().join('/')}`;
    }
    
    return holiday.date?.split('-').reverse().join('/') || '-';
  };

  // Filtrar por escopo
  const filteredHolidays = scopeFilter === 'todos' 
    ? holidays 
    : holidays.filter(h => h.scope === scopeFilter);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="feriados-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <CalendarDays className="w-6 h-6 text-blue-600" />
            Gestão de Feriados
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Configure os feriados para cálculo correto de Extra 100% (AM/Manaus)
          </p>
        </div>

        {/* Filtros e Ações */}
        <div className="flex flex-wrap items-center gap-2 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => setSelectedYear(selectedYear - 1)} className="h-9 w-9">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24 bg-white dark:bg-slate-700">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" size="icon" onClick={() => setSelectedYear(selectedYear + 1)} className="h-9 w-9">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <Select value={scopeFilter} onValueChange={setScopeFilter}>
            <SelectTrigger className="w-36 bg-white dark:bg-slate-700">
              <SelectValue placeholder="Escopo..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {scopeOptions.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1" />

          <Button
            variant="outline"
            onClick={() => loadFeriadosMoveis()}
            className="text-sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Recalcular Móveis
          </Button>

          <Button
            onClick={openCreateModal}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Feriado
          </Button>
        </div>
      </div>

      {/* Páscoa do ano */}
      {feriadosMoveis && (
        <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-amber-700 dark:text-amber-300" />
              </div>
              <div>
                <h3 className="font-bold text-amber-800 dark:text-amber-300">
                  Páscoa {selectedYear}: {feriadosMoveis.pascoa}
                </h3>
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Calculada pelo algoritmo de Computus/Gauss. Os feriados móveis derivam desta data.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {feriadosMoveis.feriados_moveis?.map((f, idx) => (
                <Badge key={idx} className="bg-amber-200 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                  {f.name}: {f.date.split('-').reverse().join('/')}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de Feriados */}
      {holidays.length === 0 ? (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-600 dark:text-slate-400 font-medium text-lg mb-4">
              Nenhum feriado cadastrado
            </p>
            <Button onClick={handleSeedHolidays} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Criar Feriados Padrão (Nacionais + AM + Manaus)
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-500" />
              {filteredHolidays.length} feriados cadastrados
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100 dark:bg-slate-900/50">
                    <TableHead className="font-bold">Nome</TableHead>
                    <TableHead className="font-bold">Data ({selectedYear})</TableHead>
                    <TableHead className="font-bold">Escopo</TableHead>
                    <TableHead className="font-bold">Tipo</TableHead>
                    <TableHead className="font-bold">Fonte Legal</TableHead>
                    <TableHead className="font-bold">Status</TableHead>
                    <TableHead className="font-bold text-center w-24">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredHolidays.map((holiday) => {
                    const ScopeIcon = scopeOptions.find(s => s.value === holiday.scope)?.icon || Globe;
                    
                    return (
                      <TableRow key={holiday.id} className={`text-sm ${!holiday.ativo ? 'opacity-50' : ''}`}>
                        <TableCell className="font-medium">
                          {holiday.name}
                          {holiday.pendente_confirmacao && (
                            <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Pendente
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className={holiday.is_movel ? 'text-blue-600 font-medium' : ''}>
                            {getDisplayDate(holiday)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${
                            holiday.scope === 'nacional' ? 'bg-blue-100 text-blue-700' :
                            holiday.scope === 'estadual' ? 'bg-green-100 text-green-700' :
                            holiday.scope === 'municipal' ? 'bg-purple-100 text-purple-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            <ScopeIcon className="w-3 h-3 mr-1" />
                            {holiday.scope}
                            {holiday.uf && ` (${holiday.uf})`}
                            {holiday.municipio && ` - ${holiday.municipio}`}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {holiday.is_movel ? (
                            <Badge className="bg-amber-100 text-amber-700">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Móvel
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700">
                              Fixo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">
                          {holiday.source || '-'}
                        </TableCell>
                        <TableCell>
                          {holiday.ativo ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Ativo
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-700">
                              Inativo
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditModal(holiday)}
                              className="h-8 w-8"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(holiday.id, holiday.name)}
                              className="h-8 w-8 text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Informações de Auditoria */}
      <Card className="bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
        <CardContent className="p-4 text-xs text-slate-600 dark:text-slate-400">
          <h4 className="font-medium text-sm mb-2 flex items-center gap-2 text-blue-700">
            <BookOpen className="w-4 h-4" />
            Sobre a Gestão de Feriados
          </h4>
          <ul className="space-y-1 list-disc list-inside">
            <li>Feriados <strong>móveis</strong> (Carnaval, Corpus Christi, etc.) são calculados automaticamente a partir da Páscoa.</li>
            <li>Feriados <strong>nacionais</strong> aplicam Extra 100% a todos os funcionários.</li>
            <li>Feriados <strong>estaduais (AM)</strong> e <strong>municipais (Manaus)</strong> aplicam apenas à sua jurisdição.</li>
            <li>Feriados <strong>facultativos</strong> não geram Extra 100% automaticamente.</li>
            <li>Todas as alterações são auditadas com usuário e data/hora.</li>
          </ul>
        </CardContent>
      </Card>

      {/* Modal de Criação/Edição */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              {editingHoliday ? 'Editar Feriado' : 'Novo Feriado'}
            </DialogTitle>
            <DialogDescription>
              Configure os dados do feriado. Feriados móveis são calculados automaticamente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome do Feriado *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Natal, Tiradentes..."
              />
            </div>

            <div className="flex items-center gap-4">
              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_movel}
                  onChange={(e) => setFormData({ ...formData, is_movel: e.target.checked, date: '' })}
                  className="w-4 h-4"
                />
                Feriado Móvel (calculado a partir da Páscoa)
              </Label>
            </div>

            {formData.is_movel ? (
              <div className="space-y-2">
                <Label>Regra de Cálculo *</Label>
                <Select value={formData.movel_rule} onValueChange={(v) => setFormData({ ...formData, movel_rule: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a regra..." />
                  </SelectTrigger>
                  <SelectContent>
                    {movelRules.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  placeholder="MM-DD (recorrente) ou YYYY-MM-DD (específico)"
                />
                <p className="text-xs text-slate-500">
                  Use MM-DD para feriados anuais (ex: 12-25 para Natal) ou YYYY-MM-DD para datas específicas.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Escopo</Label>
                <Select value={formData.scope} onValueChange={(v) => setFormData({ ...formData, scope: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {scopeOptions.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.scope === 'estadual' && (
                <div className="space-y-2">
                  <Label>UF</Label>
                  <Input
                    value={formData.uf}
                    onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase() })}
                    placeholder="AM"
                    maxLength={2}
                  />
                </div>
              )}

              {formData.scope === 'municipal' && (
                <>
                  <div className="space-y-2">
                    <Label>UF</Label>
                    <Input
                      value={formData.uf}
                      onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase() })}
                      placeholder="AM"
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Município</Label>
                    <Input
                      value={formData.municipio}
                      onChange={(e) => setFormData({ ...formData, municipio: e.target.value })}
                      placeholder="Manaus"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label>Fonte Legal (Lei/Decreto)</Label>
              <Input
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                placeholder="Ex: Lei nº 662/1949"
              />
              <p className="text-xs text-slate-500">
                Deixe em branco se não souber. Nunca invente a fonte.
              </p>
            </div>

            <div className="flex items-center gap-4">
              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.pendente_confirmacao}
                  onChange={(e) => setFormData({ ...formData, pendente_confirmacao: e.target.checked })}
                  className="w-4 h-4"
                />
                Pendente de confirmação legal
              </Label>
            </div>

            <div className="flex items-center gap-4">
              <Label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4"
                />
                Feriado ativo (aplica Extra 100%)
              </Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingHoliday ? 'Atualizar' : 'Criar'} Feriado
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeriadosPage;
